import { lazy, useEffect, useCallback, useState, Suspense } from 'react';
import './App.css';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { themeChange } from 'theme-change';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { messaging } from './firebase';
import { getToken, onMessage } from 'firebase/messaging';
import { useDispatch, useSelector } from 'react-redux';
import {
	showNotification,
	setSettings,
	setCustomSidebarItems,
} from './features/common/headerSlice';
import axios from 'axios';
import { fetchSettings } from './utils/settingsService';
import { applySettingsToCSS } from './utils/applySettings';
import { checkFirebaseSupport } from './utils/firebaseCheck';
import { getCustomSidebarItems } from './utils/customSidebarService';
import ErrorBoundary from './utils/ErrorBoundary';
import SuspenseContent from './containers/SuspenseContent';
import { identifyUser } from './utils/posthog';

function App() {
	const dispatch = useDispatch();
	const { user } = useSelector((state) => state.header);

	const firebaseLoginAnonymously = () => {
		signInAnonymously(getAuth());
	};

	const messageActivation = useCallback(async () => {
		try {
			// Check if service workers are supported
			const { isSupported } = checkFirebaseSupport();
			if (!isSupported || !('serviceWorker' in navigator)) {
				console.error('Service workers are not supported in this browser');
				return;
			}

			// Wait for the main service worker to be ready (it includes Firebase messaging)
			const registration = await navigator.serviceWorker.ready;
			console.log(
				'Using main service worker for Firebase messaging:',
				registration.scope
			);

			// Request permission and get token using the main service worker
			const token = await getToken(messaging, {
				vapidKey:
					'BHrgo_riIdB9VQLmLG_fR4bqxTMUGE6UfLfZi90W4NrTSg2j4jF7sFLGA2gwEqBkY7yyrczE3KE8pwviwL5YDkM',
				serviceWorkerRegistration: registration,
			});

			console.log('Firebase token obtained:', token ? 'Success' : 'Failed');

			if (token && token !== user?.firebaseToken) {
				await axios.post(`/api/users/${user.id}`, { firebaseToken: token });
				console.log('Firebase token updated in user profile');
			}

			// Set up foreground message handler
			onMessage(messaging, (payload) => {
				console.log('Foreground message received:', payload);

				// Display a notification for foreground messages
				if (Notification.permission === 'granted') {
					const notificationTitle = payload?.data?.title || 'New Message';
					const notificationOptions = {
						body: payload?.data?.body || '',
						icon: '/connected-logo.png',
						data: payload?.data,
					};

					new Notification(notificationTitle, notificationOptions);
				}

				// You can also update your UI here if needed
				dispatch(
					showNotification({
						message: payload.data.body || 'New notification received',
						type: 'success',
					})
				);
			});
		} catch (error) {
			console.error('Error in messageActivation:', error);
		}
	}, [user, dispatch]);

	const requestPermission = useCallback(async () => {
		try {
			let permission;
			console.log('Requesting permission...');

			// safari only
			if ('safari' in window && 'pushNotification' in window.safari) {
				const permissionData = window.safari.pushNotification.permission(
					'web.com.example.domain'
				);
				permission = permissionData?.permission;
			} else if (window.Notification) {
				permission = await window.Notification.requestPermission();
			}

			window.removeEventListener('click', requestPermission);

			console.log('permission', permission);
			switch (permission) {
				case 'granted':
					firebaseLoginAnonymously();
					messageActivation();
					break;
				case 'denied':
					break;
				default:
					break;
			}
		} catch (error) {
			console.error('Request permission error', error);
		}
	}, [messageActivation]);

	useEffect(() => {
		if (!user?.id) return;

		identifyUser(user);

		window.addEventListener('click', requestPermission);

		return () => window.removeEventListener('click', requestPermission);
	}, [dispatch, requestPermission, user]);

	useEffect(() => {
		// 👆 daisy UI themes initialization
		themeChange(false);

		const initializeSettings = async () => {
			if (window.location.hostname === import.meta.env.VITE_MANAGEMENT_URL)
				return;

			let storedSettings = localStorage.getItem('settings');
			if (storedSettings) {
				storedSettings = JSON.parse(storedSettings);
				applySettingsToCSS(storedSettings);
				dispatch(setSettings({ settings: storedSettings }));
			}

			const isLoggedIn = !!user?.id;
			const settings = await fetchSettings(isLoggedIn);

			if (settings) {
				settings.organization?.name &&
					(document.title = settings.organization.name);

				const linkElement = document.querySelector(
					'link[rel="apple-touch-icon"]'
				);
				linkElement.href = settings.companyLogo;

				const faviconElement = document.querySelector('link[rel="icon"]');
				faviconElement.href = settings.companyLogo;

				applySettingsToCSS(settings);
				localStorage.setItem('settings', JSON.stringify(settings));
				dispatch(setSettings({ settings: settings }));
			}
		};
		initializeSettings();
	}, [dispatch]);

	const fetchCustomSidebarItems = async () => {
		try {
			const response = await getCustomSidebarItems();
			dispatch(setCustomSidebarItems(response.data));
		} catch (error) {
			console.error('Error fetching custom sidebar items:', error);
		}
	};

	useEffect(() => {
		fetchSettings();
		fetchCustomSidebarItems();
	}, []);

	return (
		<ErrorBoundary>
			<Suspense fallback={<SuspenseContent />}>
				<RouterProvider router={router} />
			</Suspense>
		</ErrorBoundary>
	);
}

export default App;
